// A colony's stored-nectar **ceiling** at the playhead (WAG-100).
//
// SIM-RULES §6: a colony's pool capacity is `live cells × per-cell capacity`. Neither half is
// written into a trace as a figure — the live cells are the detail record's own cell section (a
// destroyed cell is despawned, so the section is exactly the live ones), and the multiplier rides
// the trace-manifest envelope, read off the balance the run actually recorded. So the ceiling is
// derived here, at the playhead, from the same record the stored figure is read from — which is
// what makes the two numbers in `1,283/1,530` a single, consistent reading rather than a stored
// value from this tick measured against a capacity from some other one.
//
// It moves during a run: builders add cells and raiders destroy them, so the denominator rises and
// falls with the comb. That is the point of showing it — a hive whose stores stop climbing because
// it has run out of *room* looks identical to one that has run out of forage, until the ceiling is
// on screen beside the pool.

import type { DetailRecord } from '../../net/rounds-trace';

/**
 * `colony`'s pool ceiling at `record`'s tick, or `null` when it cannot be stated.
 *
 * `null` — never a fabricated number — for a playhead with no resident record, and for a trace
 * that declares no per-cell capacity (an in-flight view that was not told one). A colony with no
 * cells left has a ceiling of `0`, which is a real and terrible answer rather than an absent one:
 * a hive with no comb can store nothing.
 */
export function playheadPoolCapacity(
  record: DetailRecord | null,
  colony: number,
  perCellCapacity: number | undefined,
): number | null {
  if (record === null || perCellCapacity === undefined || !Number.isFinite(perCellCapacity)) {
    return null;
  }
  let cells = 0;
  for (const cell of record.cells) {
    if (cell.colony === colony) {
      cells += 1;
    }
  }
  return cells * perCellCapacity;
}
